import { Badge, BodyContainer, Cell, Footer, Header, Row, Table } from 'wed-components-beta'
import { MenuState, Panel, ViewEnrollment } from './types'
import { Alert, Box, Checkbox, IconButton, Menu, Pagination, Tooltip } from '@mui/material'
import PreLoader from '../../../PreLoader'
import { Comment, MoreHoriz, Visibility, Warning } from '@mui/icons-material'
import MenuOptions from '../../../assets/MenuOptions'
import { DateTime } from 'luxon'

type Props = {
  data: ViewEnrollment[]
  isLoading: boolean
  error: boolean
  enableRowSelection: boolean
  isLoadingLookupTables: boolean
  errorLookupTables: boolean
  selectedIds: number[]
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>
  actionsTableMenu: MenuState
  setActionsTableMenu: React.Dispatch<React.SetStateAction<MenuState>>
  setPanel: React.Dispatch<React.SetStateAction<Panel>>
}

function defineColorStudentStatus(id: number) {
  switch (id) {
    case 1:
      return 'green'
    case 2:
      return 'red'
    case 8:
      return 'darkPurple'
    default:
      return 'blue'
  }
}

function defineColorCreditStatus(id: number) {
  switch (id) {
    case 1:
      return 'green'
    case 2:
      return 'orange'
    case 8:
      return 'yellow'
    default:
      return 'red'
  }
}

function onScrollLeft(e: React.UIEvent<HTMLDivElement, UIEvent>) {
  document.getElementById('body-container-right')?.scrollTo({ top: e.currentTarget.scrollTop })
}

function onScrollRight(e: React.UIEvent<HTMLDivElement, UIEvent>) {
  document.getElementById('body-container-left')?.scrollTo({ top: e.currentTarget.scrollTop })
}

export default function IssueCreditsTable({
  data,
  isLoading,
  error,
  enableRowSelection,
  isLoadingLookupTables,
  errorLookupTables,
  selectedIds,
  setSelectedIds,
  actionsTableMenu,
  setActionsTableMenu,
  setPanel
}: Props) {
  const columnWidth = [.5, 2.5, 1.25, 3, .75, .75, .75, .75, .75, 1, .75, 2, 2, 1.25, .75, .75, 1, .75]

  if (isLoading || isLoadingLookupTables) return <PreLoader />

  if (error || errorLookupTables || !data) return <Alert severity='error'>Could not load page.</Alert>
  console.log(data)
  // const meta = data.meta
  // const enrollments = data.data

  const menuOptions = [
    {
      label: 'Audit Details',
      icon: <Visibility />,
      onClick: () => setPanel({ show: true, panel: 'audit-details', id: actionsTableMenu.id })
    }
  ]

  return <>
    <Menu
      id='menu-actions'
      open={Boolean(actionsTableMenu.anchorEl)}
      anchorEl={actionsTableMenu.anchorEl}
      onClose={() => setActionsTableMenu({ anchorEl: null })}
      onClick={() => setActionsTableMenu({ anchorEl: null })}
    >
      <MenuOptions
        options={menuOptions}
      />
    </Menu>
    <Table>
      <BodyContainer
        id={'body-container-left'}
        onScroll={onScrollLeft}
        maxWidth={(columnWidth.slice(0, 2).reduce((acc, column) => acc + column, .5) * 100)}
        flexShrink={0}
      >
        <Header style={{ zIndex: 1 }}>
          {
            enableRowSelection ?
              <Cell width={columnWidth[0]} style={{ flexShrink: 0 }}>
                <Checkbox
                  id='checkbox-select-all'
                  checked={selectedIds.length === data.length && data.length > 0}
                  indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
                  onChange={(e) => setSelectedIds(e.target.checked ? data.map(enrollment => enrollment.id) : [])}
                  disableRipple
                />
              </Cell>
              :
              null
          }
          <Cell width={columnWidth[1]}>
            Student
          </Cell>
          <Cell width={.5}>
            Actions
          </Cell>
        </Header>
        {
          data.length ?
            data.map((enrollment, index) => <Row key={index} rowHeight={48}>
              {
                enableRowSelection ?
                  <Cell width={columnWidth[0]} style={{ flexShrink: 0 }}>
                    <Checkbox
                      id={`checkbox-select-${enrollment.id}`}
                      checked={selectedIds.includes(enrollment.id)}
                      onChange={(e) => setSelectedIds(e.target.checked ? [...selectedIds, enrollment.id] : selectedIds.filter(id => id !== enrollment.id))}
                      disableRipple
                    />
                  </Cell>
                  :
                  null
              }
              <Cell width={columnWidth[1]}>
                <p style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{enrollment.student_full_name}</p>
                <p>{enrollment.student.grade_name}</p>
              </Cell>
              <Cell width={.5}>
                <IconButton onClick={(e) => setActionsTableMenu({ anchorEl: e.currentTarget, id: enrollment.id })}>
                  <MoreHoriz />
                </IconButton>
              </Cell>
            </Row>)
            :
            <Row>
              <Cell flexGrow>
                No students to show.
              </Cell>
            </Row>
        }
        <Footer>
          <Cell flexGrow>
            <p>Total: {data.length} student{data.length !== 1 ? 's' : ''}</p>
          </Cell>
        </Footer>
      </BodyContainer>
      <BodyContainer
        id={'body-container-right'}
        onScroll={onScrollRight}
        flexGrow={1}
        allowCellOverflow
      >
        <Header>
          <Cell width={columnWidth[2]}>
            Student Status
          </Cell>
          <Cell width={columnWidth[3]}>
            <p>Section</p>
            <p>Student Affiliation</p>
          </Cell>
          <Cell width={columnWidth[4]}>
            Credit Type
          </Cell>
          <Cell width={columnWidth[5]}>
            Submitted
          </Cell>
          <Cell width={columnWidth[6]}>
            <p style={{ whiteSpace: 'wrap' }}>To be completed</p>
          </Cell>
          <Cell width={columnWidth[7]}>
            Total
          </Cell>
          <Cell width={columnWidth[8]}>
            Total Avg.
          </Cell>
          <Cell width={columnWidth[9]}>
            Final Exam
          </Cell>
          <Cell width={columnWidth[10]}>
            <p style={{ whiteSpace: 'wrap' }}>Final Exam Attempt</p>
          </Cell>
          <Cell width={columnWidth[11]}>
            Tutors
          </Cell>
          <Cell width={columnWidth[12]}>
            Teachers
          </Cell>
          <Cell width={columnWidth[13]}>
            Credit
          </Cell>
          <Cell width={columnWidth[14]}>
            Comment
          </Cell>
          <Cell width={columnWidth[15]}>
            <Tooltip title='student exceed page blur limit on some submissions'>
              <p>
                <div>Audit</div>
                <div>Page Blur</div>
              </p>
            </Tooltip>
          </Cell>
          <Cell width={columnWidth[16]}>
            <Tooltip title='student did not take minimum time on some submissions'>
              <p>
                <div>Audit</div>
                <div>Minimum time</div>
              </p>
            </Tooltip>
          </Cell>
          <Cell width={columnWidth[17]}>
            <Tooltip title='student got 100% on score under minimum time'>
              <p>
                <div>Audit</div>
                <div>Grade/time</div>
              </p>
            </Tooltip>
          </Cell>
          <Cell width={columnWidth[18]}>
            Is Auditing
          </Cell>
        </Header>
        {
          data.length ?
            data.map((enrollment, index) => <Row key={index} rowHeight={48}>
              <Cell width={columnWidth[2]}>
                <Badge color={defineColorStudentStatus(enrollment.student.fk_id_student_status_type)}>
                  {enrollment.student.student_status_type_name}
                </Badge>
              </Cell>
              <Cell width={columnWidth[3]}>
                <p>{enrollment.section_name}</p>
                <p>{enrollment.student.affiliation_name}</p>
              </Cell>
              <Cell width={columnWidth[4]}>
                {enrollment.section?.fk_id_credit_type !== 1 ? <Badge color='yellow'>{enrollment.section?.credit_type_tag}</Badge> : null}
              </Cell>
              <Cell width={columnWidth[5]}>
                {enrollment.report_card?.valid_submissions ?? 0}
              </Cell>
              <Cell width={columnWidth[6]}>
                <p style={{ color: (enrollment.report_card?.total_assignments ?? 0) - (enrollment.report_card?.valid_submissions ?? 0) === 0 ? '#666' : '#f66' }}>
                  {(enrollment.report_card?.total_assignments ?? 0) - (enrollment.report_card?.valid_submissions ?? 0)}
                </p>
              </Cell>
              <Cell width={columnWidth[7]}>
                {enrollment.report_card?.total_assignments ?? 0}
              </Cell>
              <Cell width={columnWidth[8]}>
                <p style={{ color: (enrollment.report_card?.final_score ?? 0) < 60 ? '#f66' : 'white' }}>
                  {enrollment.report_card?.final_score ?? '-'}
                </p>
              </Cell>
              <Cell width={columnWidth[9]}>
                <p style={{ color: (enrollment.report_card?.final_exam ?? 0) < 60 ? '#f66' : 'white' }}>
                  {enrollment.report_card?.final_exam ?? '-'}
                </p>
              </Cell>
              <Cell width={columnWidth[10]}>
                {enrollment.report_card?.final_exam_attempt ?? '-'}
              </Cell>
              <Cell width={columnWidth[11]}>
                {enrollment.faculty?.filter((faculty) => faculty.fk_id_faculty_type === 2).map((faculty, i) => <p key={i}>{faculty.faculty_full_name}</p>) ?? null}
              </Cell>
              <Cell width={columnWidth[12]}>
                {enrollment.faculty?.filter((faculty) => faculty.fk_id_faculty_type === 5).map((faculty, i) => <p key={i}>{faculty.faculty_full_name}</p>) ?? null}
              </Cell>
              <Cell width={columnWidth[13]}>
                {
                  enrollment.credit ?
                    <Badge color={defineColorCreditStatus(enrollment.credit.fk_id_credit_status_type)}>
                      {enrollment.credit.credit_status_type_name}
                    </Badge>
                    :
                    enrollment.end_date && DateTime.fromISO(String(enrollment.end_date)) < DateTime.utc() ?
                      <Badge color='strongBlue'>
                        Closing Credit
                      </Badge>
                      :
                      'Ongoing'
                }
              </Cell>
              <Cell width={columnWidth[14]}>
                {enrollment.credit?.comments && (
                  <Box display="flex" justifyContent="center">
                    <Tooltip title={enrollment.credit.comments}>
                      <IconButton>
                        <Comment />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Cell>
              <Cell width={columnWidth[15]}>
                {
                  enrollment.submissions_audit_statistics?.some((item) => item.exceed_page_blur_limit) ?
                    <AuditWarningTrue />
                    :
                    <AuditWarningFalse />
                }
              </Cell>
              <Cell width={columnWidth[16]}>
                {
                  enrollment.submissions_audit_statistics?.some((item) => item.under_minimum_time_on_attempt) ?
                    <AuditWarningTrue />
                    :
                    <AuditWarningFalse />
                }
              </Cell>
              <Cell width={columnWidth[17]}>
                {
                  enrollment.submissions_audit_statistics?.some((item) => item.suspicious_score_fast_attempt) ?
                    <AuditWarningTrue />
                    :
                    <AuditWarningFalse />
                }
              </Cell>
              <Cell width={columnWidth[18]}>
                {
                  enrollment.credit?.is_auditing ?
                    <Badge color='strongRed'>
                      Auditing
                    </Badge>
                    :
                    <AuditWarningFalse />
                }
              </Cell>
            </Row>)
            :
            <Row>
              <Cell width={columnWidth.reduce((acc, width) => acc + width, 0)} flexGrow>
                No students to show.
              </Cell>
            </Row>
        }
        <Footer>
          <Cell width={columnWidth.slice(2).reduce((acc, width) => acc + width, 0)} flexGrow>
            {/* Empty footer to match left side */}
            &nbsp;
          </Cell>
        </Footer>
      </BodyContainer>
    </Table>
  </>
}

export function AuditWarningTrue() {
  return <p style={{ textAlign: 'center' }}><Warning sx={{ color: '#FA7C5F' }} /></p>
}

export function AuditWarningFalse() {
  return <p style={{ textAlign: 'center' }}> - </p>
}
